import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

import Icon from '../../components/AppIcon';

const PrivacyPolicyPage = () => {
  const [activeTab, setActiveTab] = useState('privacy-policy');
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always enabled
    analytics: false,
    marketing: false,
    functional: false
  });
  const [preferencesUpdated, setPreferencesUpdated] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already set cookie preferences
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (!savedPreferences) {
      setShowBanner(true);
    } else {
      setCookiePreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const handleCookiePreferenceChange = (category, value) => {
    if (category === 'necessary') return; // Can't disable necessary cookies
    
    setCookiePreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const savePreferences = () => {
    const previousPreferences = localStorage.getItem('cookiePreferences');
    let previousAnalytics = false;
    
    if (previousPreferences) {
      try {
        const prev = JSON.parse(previousPreferences);
        previousAnalytics = prev?.analytics === true;
      } catch (e) {
        // Ignore parse errors
      }
    }

    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    localStorage.setItem('cookieConsentDate', new Date()?.toISOString());
    setPreferencesUpdated(true);
    setShowBanner(false);
    setTimeout(() => setPreferencesUpdated(false), 3000);

    // If analytics preference changed, reload page to apply changes
    if (previousAnalytics !== cookiePreferences?.analytics) {
      setTimeout(() => {
        window.location?.reload();
      }, 1000);
    }
  };

  const acceptAllCookies = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date()?.toISOString());
    setShowBanner(false);
    
    // Reload to initialize analytics
    setTimeout(() => {
      window.location?.reload();
    }, 500);
  };

  const declineOptionalCookies = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setCookiePreferences(necessaryOnly);
    localStorage.setItem('cookiePreferences', JSON.stringify(necessaryOnly));
    localStorage.setItem('cookieConsentDate', new Date()?.toISOString());
    setShowBanner(false);
  };

  const tabs = [
    { id: 'privacy-policy', label: 'Privacy Policy', icon: 'Shield' },
    { id: 'cookie-policy', label: 'Cookie Policy', icon: 'Cookie' },
    { id: 'cookie-preferences', label: 'Cookie Preferences', icon: 'Settings' },
    { id: 'data-rights', label: 'Your Data Rights', icon: 'FileText' }
  ];

  const cookieCategories = [
    {
      id: 'necessary',
      name: 'Necessary Cookies',
      description: 'Essential for the website to function properly. These cookies enable core functionality such as security, network management, and accessibility.',
      examples: 'Session cookies, authentication tokens, load balancing',
      canDisable: false,
      icon: 'Lock'
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      examples: 'Google Analytics, page view tracking, user behavior analysis',
      canDisable: true,
      icon: 'BarChart3'
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.',
      examples: 'LinkedIn Insight Tag, retargeting pixels, ad performance tracking',
      canDisable: true,
      icon: 'Target'
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'Enable enhanced functionality and personalization, such as videos, live chats, and preference settings.',
      examples: 'Language preferences, region settings, embedded content',
      canDisable: true,
      icon: 'Sliders'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy & Cookie Consent - Northstar Analytics</title>
        <meta 
          name="description" 
          content="Northstar Analytics privacy policy, cookie consent management, and GDPR compliance information. Learn how we protect your data and manage your privacy preferences." 
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Cookie Consent Banner */}
        {showBanner && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-primary shadow-elevation p-6 animate-slide-up">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start space-x-3 mb-2">
                    <Icon name="Cookie" size={24} className="text-secondary mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary text-lg">We Value Your Privacy</h3>
                      <p className="text-muted-foreground text-sm">
                        We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                        You can customize your preferences or accept all cookies.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveTab('cookie-preferences')}
                  >
                    Customize
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={declineOptionalCookies}
                  >
                    Decline Optional
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={acceptAllCookies}
                  >
                    Accept All
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <main className="pt-24 pb-20">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-white py-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center space-x-3 mb-4">
                <Icon name="Shield" size={32} className="text-secondary" />
                <h1 className="text-4xl lg:text-5xl font-bold">Privacy & Compliance Center</h1>
              </div>
              <p className="text-xl text-white/90 max-w-3xl">
                Your privacy matters. Learn how we collect, use, and protect your data in compliance with GDPR and Irish Data Protection Act.
              </p>
            </div>
          </section>

          {/* Tab Navigation */}
          <section className="bg-white border-b border-border sticky top-16 z-40">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 px-6 py-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'border-secondary text-secondary' :'border-transparent text-muted-foreground hover:text-primary'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Content Sections */}
          <section className="py-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Privacy Policy Tab */}
              {activeTab === 'privacy-policy' && (
                <div className="bg-white rounded-2xl shadow-elevation p-8 lg:p-12 space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold text-primary mb-4">Privacy Policy</h2>
                    <p className="text-muted-foreground mb-2">Last Updated: January 23, 2026</p>
                    <p className="text-muted-foreground">Effective Date: January 1, 2026</p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">1. Introduction</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        Northstar Analytics ("we," "our," or "us") is committed to protecting your privacy and personal data. 
                        This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit 
                        our website or use our services, in compliance with the General Data Protection Regulation (GDPR) and the 
                        Irish Data Protection Act 2018.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">2. Data Controller Information</h3>
                      <div className="bg-surface rounded-lg p-4 space-y-2">
                        <p className="text-muted-foreground"><strong>Company Name:</strong> Northstar Analytics</p>
                        <p className="text-muted-foreground"><strong>Registered Address:</strong> Dublin, Ireland</p>
                        <p className="text-muted-foreground"><strong>Contact Email:</strong> privacy@northstaranalytics.eu</p>
                        <p className="text-muted-foreground"><strong>Data Protection Officer:</strong> dpo@northstaranalytics.eu</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">3. Information We Collect</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-primary mb-2">3.1 Personal Information</h4>
                          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                            <li>Name, email address, phone number</li>
                            <li>Company name, job title, industry</li>
                            <li>Assessment responses and consultation booking details</li>
                            <li>Newsletter subscription preferences</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-primary mb-2">3.2 Technical Information</h4>
                          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                            <li>IP address, browser type, device information</li>
                            <li>Pages visited, time spent on pages, referral sources</li>
                            <li>Cookies and similar tracking technologies</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">4. How We Use Your Information</h3>
                      <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                        <li>To provide and improve our data strategy consulting services</li>
                        <li>To process consultation bookings and assessment requests</li>
                        <li>To send newsletters and marketing communications (with your consent)</li>
                        <li>To analyze website usage and optimize user experience</li>
                        <li>To comply with legal obligations and protect our rights</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">5. Legal Basis for Processing</h3>
                      <p className="text-muted-foreground mb-3">We process your personal data based on:</p>
                      <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                        <li><strong>Consent:</strong> Newsletter subscriptions, marketing communications</li>
                        <li><strong>Contract Performance:</strong> Consultation bookings, service delivery</li>
                        <li><strong>Legitimate Interests:</strong> Website analytics, service improvement</li>
                        <li><strong>Legal Obligation:</strong> Compliance with tax and regulatory requirements</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">6. Data Sharing and Disclosure</h3>
                      <p className="text-muted-foreground mb-3">We may share your information with:</p>
                      <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                        <li><strong>Service Providers:</strong> Supabase (database), Resend (email), Google Analytics</li>
                        <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                        <li><strong>Business Transfers:</strong> In connection with mergers or acquisitions</li>
                      </ul>
                      <p className="text-muted-foreground mt-3">
                        We do not sell your personal data to third parties.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">7. Data Retention</h3>
                      <p className="text-muted-foreground">
                        We retain your personal data only as long as necessary for the purposes outlined in this policy or as 
                        required by law. Assessment data is retained for 3 years, consultation records for 7 years (tax compliance), 
                        and marketing consent until withdrawn.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">8. International Data Transfers</h3>
                      <p className="text-muted-foreground">
                        Your data may be transferred to and processed in countries outside the European Economic Area (EEA). 
                        We ensure appropriate safeguards are in place, including Standard Contractual Clauses and adequacy decisions.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">9. Security Measures</h3>
                      <p className="text-muted-foreground">
                        We implement industry-standard security measures including encryption, secure servers, access controls, 
                        and regular security audits to protect your data from unauthorized access, alteration, or destruction.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">10. Changes to This Policy</h3>
                      <p className="text-muted-foreground">
                        We may update this Privacy Policy periodically. We will notify you of significant changes via email or 
                        website notice. Continued use of our services after changes constitutes acceptance.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cookie Policy Tab */}
              {activeTab === 'cookie-policy' && (
                <div className="bg-white rounded-2xl shadow-elevation p-8 lg:p-12 space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold text-primary mb-4">Cookie Policy</h2>
                    <p className="text-muted-foreground">Understanding how we use cookies and similar technologies</p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">What Are Cookies?</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        Cookies are small text files stored on your device when you visit our website. They help us provide a better 
                        user experience by remembering your preferences, analyzing site usage, and enabling certain functionality.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">Cookie Categories</h3>
                      <div className="space-y-4">
                        {cookieCategories?.map((category) => (
                          <div key={category?.id} className="bg-surface rounded-lg p-6">
                            <div className="flex items-start space-x-4">
                              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Icon name={category?.icon} size={24} className="text-secondary" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-primary mb-2">{category?.name}</h4>
                                <p className="text-muted-foreground text-sm mb-3">{category?.description}</p>
                                <div className="text-xs text-muted-foreground">
                                  <strong>Examples:</strong> {category?.examples}
                                </div>
                                {!category?.canDisable && (
                                  <div className="mt-3 inline-flex items-center space-x-2 bg-primary/10 text-primary rounded-full px-3 py-1 text-xs">
                                    <Icon name="Lock" size={12} />
                                    <span>Always Active</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-3">Managing Cookies</h3>
                      <p className="text-muted-foreground mb-4">
                        You can control cookie preferences through our Cookie Preferences center or your browser settings. 
                        Note that disabling certain cookies may affect website functionality.
                      </p>
                      <Button
                        variant="default"
                        iconName="Settings"
                        onClick={() => setActiveTab('cookie-preferences')}
                      >
                        Manage Cookie Preferences
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Cookie Preferences Tab */}
              {activeTab === 'cookie-preferences' && (
                <div className="bg-white rounded-2xl shadow-elevation p-8 lg:p-12">
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold text-primary mb-4">Cookie Preferences</h2>
                    <p className="text-muted-foreground">Customize your cookie settings and manage your consent</p>
                  </div>

                  {preferencesUpdated && (
                    <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6 flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} className="text-success" />
                      <p className="text-success font-medium">Your preferences have been saved successfully!</p>
                    </div>
                  )}

                  <div className="space-y-6 mb-8">
                    {cookieCategories?.map((category) => (
                      <div key={category?.id} className="border border-border rounded-lg p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start space-x-4 flex-1">
                            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Icon name={category?.icon} size={20} className="text-secondary" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-primary mb-2">{category?.name}</h3>
                              <p className="text-muted-foreground text-sm mb-2">{category?.description}</p>
                              <p className="text-xs text-muted-foreground">
                                <strong>Used for:</strong> {category?.examples}
                              </p>
                            </div>
                          </div>
                          <div className="ml-4">
                            {category?.canDisable ? (
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={cookiePreferences?.[category?.id]}
                                  onChange={(e) => handleCookiePreferenceChange(category?.id, e?.target?.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-secondary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-secondary"></div>
                              </label>
                            ) : (
                              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary rounded-full px-3 py-1 text-xs">
                                <Icon name="Lock" size={12} />
                                <span>Required</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      variant="default"
                      size="lg"
                      iconName="Save"
                      onClick={savePreferences}
                    >
                      Save Preferences
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={acceptAllCookies}
                    >
                      Accept All Cookies
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={declineOptionalCookies}
                    >
                      Decline Optional Cookies
                    </Button>
                  </div>

                  <div className="mt-8 p-4 bg-surface rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Consent Date:</strong> {localStorage.getItem('cookieConsentDate') 
                        ? new Date(localStorage.getItem('cookieConsentDate'))?.toLocaleDateString('en-IE', { 
                            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                          })
                        : 'Not set'}
                    </p>
                  </div>
                </div>
              )}

              {/* Data Rights Tab */}
              {activeTab === 'data-rights' && (
                <div className="bg-white rounded-2xl shadow-elevation p-8 lg:p-12 space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold text-primary mb-4">Your Data Rights Under GDPR</h2>
                    <p className="text-muted-foreground">You have the following rights regarding your personal data</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-surface rounded-lg p-6">
                      <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon name="Eye" size={24} className="text-secondary" />
                      </div>
                      <h3 className="font-semibold text-primary mb-2">Right to Access</h3>
                      <p className="text-muted-foreground text-sm">
                        Request a copy of all personal data we hold about you, including how it's used and who it's shared with.
                      </p>
                    </div>

                    <div className="bg-surface rounded-lg p-6">
                      <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon name="Edit" size={24} className="text-secondary" />
                      </div>
                      <h3 className="font-semibold text-primary mb-2">Right to Rectification</h3>
                      <p className="text-muted-foreground text-sm">
                        Correct any inaccurate or incomplete personal data we hold about you.
                      </p>
                    </div>

                    <div className="bg-surface rounded-lg p-6">
                      <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon name="Trash2" size={24} className="text-secondary" />
                      </div>
                      <h3 className="font-semibold text-primary mb-2">Right to Erasure</h3>
                      <p className="text-muted-foreground text-sm">
                        Request deletion of your personal data when it's no longer necessary or you withdraw consent.
                      </p>
                    </div>

                    <div className="bg-surface rounded-lg p-6">
                      <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon name="Download" size={24} className="text-secondary" />
                      </div>
                      <h3 className="font-semibold text-primary mb-2">Right to Data Portability</h3>
                      <p className="text-muted-foreground text-sm">
                        Receive your personal data in a structured, machine-readable format and transfer it to another controller.
                      </p>
                    </div>

                    <div className="bg-surface rounded-lg p-6">
                      <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon name="Ban" size={24} className="text-secondary" />
                      </div>
                      <h3 className="font-semibold text-primary mb-2">Right to Object</h3>
                      <p className="text-muted-foreground text-sm">
                        Object to processing of your personal data for direct marketing or legitimate interests.
                      </p>
                    </div>

                    <div className="bg-surface rounded-lg p-6">
                      <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon name="Pause" size={24} className="text-secondary" />
                      </div>
                      <h3 className="font-semibold text-primary mb-2">Right to Restriction</h3>
                      <p className="text-muted-foreground text-sm">
                        Request restriction of processing while we verify accuracy or assess your objection.
                      </p>
                    </div>
                  </div>

                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                    <h3 className="font-semibold text-primary mb-4 flex items-center space-x-2">
                      <Icon name="Mail" size={20} />
                      <span>Exercise Your Rights</span>
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      To exercise any of these rights, please contact our Data Protection Officer at:
                    </p>
                    <div className="space-y-2 mb-6">
                      <p className="text-muted-foreground">
                        <strong>Email:</strong> <a href="mailto:dpo@northstaranalytics.eu" className="text-secondary hover:underline">dpo@northstaranalytics.eu</a>
                      </p>
                      <p className="text-muted-foreground">
                        <strong>Response Time:</strong> We will respond within 30 days of receiving your request.
                      </p>
                    </div>
                    <Button
                      variant="default"
                      iconName="Send"
                      onClick={() => window.location.href = 'mailto:dpo@northstaranalytics.eu?subject=Data Rights Request'}
                    >
                      Submit Data Rights Request
                    </Button>
                  </div>

                  <div className="bg-surface rounded-lg p-6">
                    <h3 className="font-semibold text-primary mb-3">Right to Lodge a Complaint</h3>
                    <p className="text-muted-foreground mb-4">
                      If you believe we have not handled your personal data properly, you have the right to lodge a complaint 
                      with the Irish Data Protection Commission:
                    </p>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p><strong>Data Protection Commission</strong></p>
                      <p>21 Fitzwilliam Square South, Dublin 2, D02 RD28, Ireland</p>
                      <p>Phone: +353 (0)761 104 800</p>
                      <p>Email: <a href="mailto:info@dataprotection.ie" className="text-secondary hover:underline">info@dataprotection.ie</a></p>
                      <p>Website: <a href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">www.dataprotection.ie</a></p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;